import React, { useState, useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import { Fieldset } from "primereact/fieldset";
import { Button } from "primereact/button";
import { useFormik } from "formik";
import { classNames } from "primereact/utils";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import ModalCodigo from "./ModalCodigo";
import { Calendar } from "primereact/calendar";
import {
  appConstantes,
  nemonicoFlujo,
  nemonicoIdentificacion,
  nemonicoProceso,
  nemonicoSistema,
} from "../../constantes/appConstantes";
import { Message } from "primereact/message";
import { useNavigate } from "react-router-dom";
import {
  enviarCorreo,
  obtenerNombres,
  obtenerTiposIdentificacion,
  obtenerTratamientoDatos,
  setCorreo,
  setCorreoVerificado,
  setMessageError,
  setNombres,
  setPersona,
  setTipoIdentificacion,
  setTratamientoDatos,
  validarPersona,
  verificarCorreo,
} from "../../sliceRedux/Reserva.slice";
import useFullPageLoader from "react-comun-seps/lib/components/FullPageLoaderHooks/useFullPageLoader";
import { useDispatch, useSelector } from "react-redux";
import RenderToast from "../../componentes/RenderToast";
import { Divider } from "primereact/divider";
import ModalTratamientoDatos from "./ModalTratamientoDatos";
import { setCalendarLanguage } from "../../helpers/locale";

const Reserva = () => {
  const toast = useRef(null);
  const dispatch = useDispatch();
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [modal, setModal] = useState(false);
  const [modalTratamiento, setModalTratamiento] = useState(false);
  const [validate, setValidate] = useState(1);
  const navigate = useNavigate();
  const messageError = useSelector((state) => state.reserva.messageError);
  const nombres = useSelector((state) => state.reserva.nombres);
  const validarDatos = useSelector((state) => state.reserva.persona);
  const correoEnviado = useSelector((state) => state.reserva.correo);
  const correoVerificado = useSelector((state) => state.reserva.correoVerificado);
  const tratamientoDatos = useSelector((state) => state.reserva.tratamientoDatos);
  const listaTiposIdentificacion = useSelector((state) => state.reserva.listaTiposIdentificacion);

  setCalendarLanguage("es");
  useEffect(() => {
    showLoader();
    dispatch(obtenerTiposIdentificacion());
  }, []);

  const validationSchema = Yup.object().shape({
    tipoIdentificacion: Yup.string().required("Campo requerido."),
    identificacion: Yup.string().required("Campo requerido."),
    nombres: Yup.string().required("Campo requerido."),
    celular: Yup.string()
      .required("Campo requerido.")
      .matches(/^09\d{8}$/, "El número de celular debe empezar con 09 y tener 10 dígitos."),
    email: Yup.string()
      .email("Formato de correo electrónico no válido.")
      .required("Campo requerido."),
  });

  const formik = useFormik({
    initialValues: {
      tipoIdentificacion: "",
      identificacion: "",
      nombres: "",
      telefono: "",
      celular: "",
      email: "",
      codigoDactilar: "",
      campoValidacion: "",
      listaCampos: {
        fechaNacimiento_1: "",
        codigoDactilar_1: "",
        fechaCaducidad_1: "",
      },
      idTipoIdentificacion: 0,
      tratamientoDatos: {},
      croquis: null,
    },
    validationSchema: validationSchema,
    onSubmit: (data) => {
      showLoader();
      let dataSend = {
        nemProceso: nemonicoProceso.SOLICITUD_RESERVA_DENOM,
        numIdentificacion: formik.values.identificacion,
        idTipoIdentificacion: formik.values.idTipoIdentificacion,
        correo: formik.values.email,
        nombreCompleto: formik.values.nombres,
      };
      dispatch(enviarCorreo(dataSend));
    },
  });

  useEffect(() => {
    if (listaTiposIdentificacion.code === 200) {
      hideLoader();
    }
    return () => {
      dispatch(setTipoIdentificacion({ ...listaTiposIdentificacion, ...{ code: 0 } }));
    };
  }, [listaTiposIdentificacion.code]);

  useEffect(() => {
    if (nombres.code === 200) {
      formik.setFieldValue("nombres", nombres?.object?.nombreCompleto ?? "");
      hideLoader();
    }
    return () => {
      dispatch(setNombres({ ...nombres, ...{ code: 0 } }));
    };
  }, [nombres.code]);

  useEffect(() => {
    if (tratamientoDatos.code === 200) {
      toggleModal();
      formik.setFieldValue("tratamientoDatos", tratamientoDatos?.object ?? {});
      toggleModalTratamiento();
      hideLoader();
    }
    return () => {
      dispatch(setTratamientoDatos({ ...tratamientoDatos, ...{ code: 0 } }));
    };
  }, [tratamientoDatos.code]);

  useEffect(() => {
    if (validarDatos.code === 200) {
      if (formik.values.campoValidacion.length > 0) {
        let data = {
          ...formik.values.listaCampos,
          [formik.values.campoValidacion]: "success",
        };
        formik.setFieldValue("listaCampos", data);
      }
      hideLoader();
    }
    return () => {
      dispatch(setPersona({ ...validarDatos, ...{ code: 0 } }));
    };
  }, [validarDatos.code]);

  useEffect(() => {
    if (messageError != "") {
      RenderToast(toast, "error", "Error", messageError);
      if (formik.values.campoValidacion.length > 0) {
        let data = {
          ...formik.values.listaCampos,
          [formik.values.campoValidacion]: "error",
        };
        formik.setFieldValue("listaCampos", data);
      }
    }
    return () => {
      hideLoader();
      dispatch(setMessageError(""));
    };
  }, [messageError]);

  const getFormErrorMessage = (name) => {
    return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
  };

  const isFormFieldValid = (name) => {
    const isValid = !!(formik.touched[name] && formik.errors[name]);
    return isValid;
  };

  const handleCaptchaVerify = () => {
    if (formik.values.tipoIdentificacion === nemonicoIdentificacion.CEDULA) {
      setValidate(2);
    } else {
      setValidate(3);
    }
  };
  const toggleModal = () => {
    setModal(!modal);
  };

  const toggleModalTratamiento = () => {
    setModalTratamiento(!modalTratamiento);
  };
  const callbackSave = (param) => {
    showLoader();
    let data = {
      nemProceso: nemonicoProceso.SOLICITUD_RESERVA_DENOM,
      numIdentificacion: formik.values.identificacion,
      idTipoIdentificacion: formik.values.idTipoIdentificacion,
      codigoValidacion: param.codigo,
    };
    dispatch(verificarCorreo(data));
  };

  const callbackSaveTratamiento = () => {
    showLoader();
    sessionStorage.setItem("userData", JSON.stringify({ autenticate: true }));
    navigate("/solicitudes", { state: { datos: formik.values } });
  };

  useEffect(() => {
    if (correoEnviado.code === 200) {
      toggleModal();
      hideLoader();
    }
    return () => {
      dispatch(setCorreo({ ...correoEnviado, ...{ code: 0 } }));
    };
  }, [correoEnviado.code]);

  useEffect(() => {
    if (correoVerificado.code === 200) {
      let valData = {
        nemonicoSistema: nemonicoSistema.SICCA,
        nemonicoProceso: nemonicoProceso.CAJAS,
        nemonicoFlujo: nemonicoFlujo.FLUARESENT,
        numeroIdentificacion: formik.values.identificacion,
        nombrePersona: formik.values.nombres,
        ruc: null,
        razonSocial: null,
        calidadComparece: null,
        correo: formik.values.email,
      };
      dispatch(obtenerTratamientoDatos(valData));
    }
    return () => {
      dispatch(setCorreoVerificado({ ...correoVerificado, ...{ code: 0 } }));
    };
  }, [correoVerificado.code]);

  return (
    <>
      {loader}
      <Toast ref={toast} />
      <div className="card">
        <div className="text-center">
          <p className="font-semibold">Solicitud de Reserva de Denominación</p>
        </div>

        <Fieldset legend="Datos personales">
          <form
            onSubmit={formik.handleSubmit}
            className="flex flex-column align-items-center w-100"
            style={{ margin: "0 auto" }}
          >
            <div className="flex align-items-center col-12 md:col-5 ">
              <label
                htmlFor="tipoIdentificacion"
                className="mr-2"
                style={{ minWidth: "180px", textAlign: "left" }}
                required
              >
                Tipo de identificación:
              </label>
              <Dropdown
                id="tipoIdentificacion"
                name="tipoIdentificacion"
                optionLabel="nomCatalogo"
                optionValue="nomNemonico"
                placeholder="Seleccione un tipo de documento"
                options={listaTiposIdentificacion?.object || []}
                value={formik.values.tipoIdentificacion}
                emptyMessage={appConstantes.SIN_DATOS}
                className={classNames({
                  "p-invalid": isFormFieldValid("tipoIdentificacion"),
                })}
                onChange={(e) => {
                  formik.resetForm();
                  formik.handleChange(e);
                  let tipo = listaTiposIdentificacion?.object?.find(
                    (item) => item.nomNemonico === e.value
                  );
                  formik.setFieldValue("idTipoIdentificacion", tipo?.codCatalogo ?? 0);
                  formik.setFieldValue("nomCatalogoIdentificacion", tipo?.nomCatalogo ?? "");
                  setValidate(1);
                }}
                style={{ width: "100%" }}
              />
            </div>
            {getFormErrorMessage("tipoIdentificacion")}

            <div className="flex align-items-center col-12 md:col-5 ">
              <label
                htmlFor="identificacion"
                className="mr-2"
                style={{ minWidth: "180px", textAlign: "left" }}
                required
              >
                Identificación:
              </label>
              <InputText
                id="identificacion"
                name="identificacion"
                value={formik.values.identificacion}
                placeholder="Digite su número de identificación"
                autoComplete="off"
                maxLength={
                  formik.values.tipoIdentificacion === nemonicoIdentificacion.CEDULA ? 10 : 20
                }
                keyfilter={
                  formik.values.tipoIdentificacion === nemonicoIdentificacion.CEDULA
                    ? "int"
                    : "alphanum"
                }
                className={classNames({
                  "p-invalid": isFormFieldValid("identificacion"),
                })}
                onChange={(e) => {
                  formik.setFieldValue("identificacion", e.target.value.toUpperCase());
                }}
                disabled={validate != 1}
                onBlur={(e) => {
                  if (
                    formik.values.tipoIdentificacion === nemonicoIdentificacion.CEDULA &&
                    formik.values.identificacion.length > 0
                  ) {
                    showLoader();
                    dispatch(obtenerNombres(formik.values.identificacion));
                  }
                }}
                style={{ width: "100%" }}
              />
            </div>
            {getFormErrorMessage("identificacion")}

            <div className="flex align-items-center col-12 md:col-5 ">
              <label
                htmlFor="nombres"
                className="mr-2"
                style={{ minWidth: "180px", textAlign: "left" }}
                required
              >
                Apellidos y Nombres:
              </label>
              <InputText
                id="nombres"
                name="nombres"
                value={formik.values.nombres}
                autoComplete="off"
                keyfilter={/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/}
                placeholder="Apellidos y Nombres"
                disabled={formik.values.tipoIdentificacion === nemonicoIdentificacion.CEDULA}
                maxLength={150}
                className={classNames("w-full", {
                  "p-invalid": isFormFieldValid("nombres"),
                })}
                onChange={(e) => {
                  formik.setFieldValue("nombres", e.target.value.toUpperCase());
                }}
              />
            </div>
            {getFormErrorMessage("nombres")}

            {formik.values.tipoIdentificacion.length > 0 &&
              formik.values.identificacion.length > 0 &&
              formik.values.nombres.length > 0 &&
              validate === 1 && (
                <div className="flex align-items-center w-100 ">
                  <HCaptcha
                    sitekey="79ff2a71-b636-4833-af19-d53fd93eff78"
                    onVerify={handleCaptchaVerify}
                  />
                </div>
              )}

            {validate === 2 && (
              <>
                <Divider />
                <div className="text-center">
                  <div className="col-12 md:col-12">
                    <Message
                      severity="info"
                      text="Para ingresar los siguientes datos tenga a la mano su cédula de identidad."
                    />
                  </div>
                  <div className="field col-12 md:col-12">
                    <Message
                      severity="warn"
                      text="Debe tener al menos 2 campos remitidos para poder validar los datos de la identificación"
                    />
                  </div>
                </div>
                <div className="flex align-items-center col-12 md:col-5 ">
                  <label
                    htmlFor="fechaNacimiento"
                    className="mr-2"
                    style={{ minWidth: "180px", textAlign: "left" }}
                  >
                    Fecha de nacimiento:
                  </label>
                  <Calendar
                    id="fechaNacimiento"
                    name="fechaNacimiento"
                    value={formik.values.fechaNacimiento}
                    onChange={(e) => {
                      formik.setFieldValue("fechaNacimiento", e.value);
                      formik.setFieldValue("campoValidacion", "fechaNacimiento_1");
                      showLoader();
                      let data = {
                        identificacion: formik.values.identificacion,
                        fechaNacimiento: e.value,
                      };
                      dispatch(validarPersona(data));
                    }}
                    dateFormat="dd/mm/yy"
                    readOnlyInput
                    showIcon
                    style={{ width: "100%" }}
                    locale="es"
                  />
                  <Message
                    style={{ background: "transparent" }}
                    severity={formik.values.listaCampos["fechaNacimiento_1"]}
                  />
                </div>
                <div className="flex align-items-center col-12 md:col-5 ">
                  <label
                    htmlFor="codigoDactilar"
                    className="mr-2"
                    style={{ minWidth: "180px", textAlign: "left" }}
                  >
                    Código dactilar:
                  </label>
                  <InputText
                    id="codigoDactilar"
                    name="codigoDactilar"
                    value={formik.values.codigoDactilar}
                    placeholder="Digite el código dactilar de su cédula de identidad"
                    autoComplete="off"
                    keyfilter={"alphanum"}
                    maxLength={15}
                    onChange={(e) => {
                      formik.setFieldValue("codigoDactilar", e.target.value.toUpperCase());
                    }}
                    onBlur={(e) => {
                      if (formik.values.codigoDactilar.length > 0) {
                        formik.setFieldValue("campoValidacion", "codigoDactilar_1");
                        showLoader();
                        let data = {
                          identificacion: formik.values.identificacion,
                          individualDactilar: formik.values.codigoDactilar,
                        };
                        dispatch(validarPersona(data));
                      }
                    }}
                    style={{ width: "100%" }}
                  />
                  <Message
                    style={{ background: "transparent" }}
                    severity={formik.values.listaCampos["codigoDactilar_1"]}
                  />
                </div>
                <div className="flex align-items-center col-12 md:col-5 ">
                  <label
                    htmlFor="fechaCaducidad"
                    className="mr-2"
                    style={{ minWidth: "180px", textAlign: "left" }}
                  >
                    Fecha de caducidad:
                  </label>
                  <Calendar
                    id="fechaCaducidad"
                    name="fechaCaducidad"
                    value={formik.values.fechaCaducidad}
                    readOnlyInput
                    onChange={(e) => {
                      formik.setFieldValue("fechaCaducidad", e.value);
                      formik.setFieldValue("campoValidacion", "fechaCaducidad_1");
                      showLoader();
                      let data = {
                        identificacion: formik.values.identificacion,
                        fechaExpiracion: e.value,
                      };
                      dispatch(validarPersona(data));
                    }}
                    dateFormat="dd/mm/yy"
                    showIcon
                    style={{ width: "100%" }}
                    locale="es"
                  />

                  <Message
                    style={{ background: "transparent" }}
                    severity={formik.values.listaCampos["fechaCaducidad_1"]}
                  />
                </div>
                <Divider />
              </>
            )}

            {validate >= 3 && (
              <>
                <Divider />
                <div className="flex align-items-center col-12 md:col-5 ">
                  <label
                    htmlFor="telefono"
                    className="mr-2"
                    style={{ minWidth: "180px", textAlign: "left" }}
                  >
                    Teléfono:
                  </label>
                  <InputText
                    id="telefono"
                    name="telefono"
                    keyfilter="int"
                    maxLength={10}
                    autoComplete="off"
                    value={formik.values.telefono}
                    className={classNames({
                      "p-invalid": isFormFieldValid("telefono"),
                    })}
                    onChange={(e) => {
                      formik.handleChange(e);
                    }}
                    style={{ width: "100%" }}
                  />
                </div>
                {getFormErrorMessage("telefono")}

                <div className="flex align-items-center col-12 md:col-5 ">
                  <label
                    htmlFor="celular"
                    className="mr-2"
                    style={{ minWidth: "180px", textAlign: "left" }}
                    required
                  >
                    Celular:
                  </label>
                  <InputText
                    id="celular"
                    name="celular"
                    keyfilter="int"
                    maxLength={10}
                    autoComplete="off"
                    value={formik.values.celular}
                    className={classNames({
                      "p-invalid": isFormFieldValid("celular"),
                    })}
                    onChange={(e) => {
                      formik.handleChange(e);
                    }}
                    style={{ width: "100%" }}
                  />
                </div>
                {getFormErrorMessage("celular")}

                <div className="flex align-items-center col-12 md:col-5 ">
                  <label
                    htmlFor="email"
                    className="mr-2"
                    style={{ minWidth: "180px", textAlign: "left" }}
                    required
                  >
                    Correo electrónico:
                  </label>
                  <InputText
                    id="email"
                    name="email"
                    value={formik.values.email}
                    autoComplete="off"
                    className={classNames({
                      "p-invalid": isFormFieldValid("email"),
                    })}
                    onChange={(e) => {
                      formik.handleChange(e);
                    }}
                    maxLength={100}
                    onBlur={(e) => {
                      if (formik.values.email.length > 0) {
                        confirmDialog({
                          message: (
                            <span>
                              Declaro que soy la persona responsable de ejercer las funciones de
                              Representación legal, una vez que se realice el proceso de
                              constitución de la Entidad.
                              <br />
                              <br />
                              Confirmo que el correo que he proporcionado es válido y que recibiré a
                              través de este medio las notificaciones que correspondan para
                              continuar con el proceso de solicitud de reserva de denominación.
                            </span>
                          ),
                          header: "Información",
                          style: { width: "650px" },
                          icon: "pi pi-exclamation-triangle",
                          className: "p-text-justify",
                          rejectLabel: "Cancelar",
                          acceptLabel: "Aceptar",
                          accept: () => setValidate(4),
                          reject: () => setValidate(3),
                        });
                      }
                    }}
                    style={{ width: "100%" }}
                  />
                </div>
                {getFormErrorMessage("email")}
                <Divider />
              </>
            )}
            <Button
              label="Continuar"
              icon="pi pi-chevron-circle-right"
              iconPos="right"
              type="button"
              visible={validate === 2}
              disabled={
                Object.values(formik.values.listaCampos).filter((val) => val === "success").length <
                2
              }
              onClick={() => setValidate(3)}
              className="mt-3"
            />
            <Button
              label="Continuar"
              icon="pi pi-chevron-circle-right"
              iconPos="right"
              type="button"
              visible={validate >= 3}
              disabled={validate === 3}
              onClick={() => formik.handleSubmit()}
              className="mt-3"
            />
          </form>
        </Fieldset>

        {modal && (
          <ModalCodigo
            modal={modal}
            callbackModal={toggleModal}
            callbackSave={callbackSave}
            email={formik.values.email}
          />
        )}

        {modalTratamiento && (
          <ModalTratamientoDatos
            modal={modalTratamiento}
            texto={tratamientoDatos?.object?.txtPlantillaAcuerdo ?? ""}
            callbackModal={toggleModalTratamiento}
            callbackSave={callbackSaveTratamiento}
          />
        )}
      </div>
      <ConfirmDialog />
    </>
  );
};
export default Reserva;
